import React, { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  barFraction,
  buildExpenseSeries,
  expenseTotalsByHead,
  fmt,
  formatDate,
  maxBucketRevenue,
  maxHeadTotal,
  periodRange,
  profitFor,
  scopeToPeriod,
  summariseExpenses,
  toDateInput,
  useAppStore,
} from '@restorant/core';
import type { DateRange, Expense, ExpenseCategory, Period } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT } from '../i18n';
import {
  useExpenseCategories, useExpenseHeadIcon, useExpenseHeadLabel, useExpenses,
} from '../menu';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { Screen } from '../components/Screen';
import { BarChart } from '../components/BarChart';
import { BarTrack } from '../components/BarRow';
import { EmptyNote } from '../components/EmptyNote';
import { ExpenseEditor } from '../components/ExpenseEditor';
import { ExpenseHeadEditor } from '../components/ExpenseHeadEditor';
import { Panel } from '../components/Panel';
import { SectionTitle } from '../components/SectionTitle';
import { StatTile } from '../components/StatTile';
import { T } from '../components/Text';
import {
  PeriodTabs, RangePicker, daysAgo, usePeriodLabel,
} from '../components/PeriodPicker';

/**
 * A payment out, one row.
 *
 * The head and the date lead, because that is what an owner scans for when
 * they are looking for "the vegetable bill from Tuesday". The amount sits hard
 * right in gold, aligned down the column, so the list can be read as a figure
 * without reading it as sentences.
 */
function ExpenseRow({ expense, onPress }: { expense: Expense; onPress: () => void }) {
  const colors = useColors();
  const t = useT();
  const headLabel = useExpenseHeadLabel();
  const headIcon = useExpenseHeadIcon();

  const detail = [
    formatDate(new Date(expense.paidAt)),
    expense.payee || null,
    expense.note || null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Pressable
      onPress={onPress}
      testID={`expense-${expense.id}`}
      style={({ hovered }: any) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: hovered ? colors.goldDim : colors.line,
        backgroundColor: colors.surface,
        marginBottom: 8,
      })}
    >
      <Icon name={headIcon(expense.categoryId) as IconName} size={18} color={colors.textFaint} />
      <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
        <T size={13.5} weight={700} numberOfLines={1}>
          {headLabel(expense.categoryId) || t('uncategorised')}
        </T>
        <T size={11.5} color={colors.textFaint} numberOfLines={1}>
          {detail}
        </T>
      </View>
      <T size={14} weight={700} color={colors.gold}>
        {fmt(expense.amount)}
      </T>
      {/* The same dot the sync badge uses: this one has not reached the
          server yet. Silent on an entry that has. */}
      {expense.synced ? null : (
        <View
          style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: colors.goldDim }}
        />
      )}
    </Pressable>
  );
}

/** One spending head in the manage list. */
function HeadRow({ head, onEdit }: { head: ExpenseCategory; onEdit: () => void }) {
  const colors = useColors();
  const t = useT();
  return (
    <Pressable
      onPress={onEdit}
      style={({ hovered }: any) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 11,
        paddingHorizontal: 14,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: hovered ? colors.goldDim : colors.line,
        backgroundColor: colors.surface,
        marginBottom: 8,
        opacity: head.active ? 1 : 0.55,
      })}
    >
      <Icon name={head.icon as IconName} size={17} color={colors.textFaint} />
      <T size={13.5} weight={700} style={{ flex: 1 }} numberOfLines={1}>
        {head.name}
      </T>
      {head.active ? null : (
        <T size={11.5} color={colors.textFaint}>
          {t('hidden_label')}
        </T>
      )}
      <Icon name="edit" size={15} color={colors.textFaint} />
    </Pressable>
  );
}

/** A dashed "add one" button, matching the floor-plan editor's. */
function AddButton({
  label,
  onPress,
  testID,
}: {
  label: string;
  onPress: () => void;
  testID: string;
}) {
  const colors = useColors();
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      style={({ hovered }: any) => ({
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 9,
        marginTop: 4,
        paddingVertical: 15,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: hovered ? colors.gold : colors.line,
      })}
    >
      <Icon name="plus" size={17} color={colors.textDim} />
      <T size={13.5} weight={700} color={colors.textDim}>
        {label}
      </T>
    </Pressable>
  );
}

/**
 * What the shop spends, and what it therefore actually made.
 *
 * Owner-only. The tab is not rendered for anyone else (see `useNavItems` in
 * App.tsx) and the two tables behind it are owner-only in RLS, so a worker who
 * reached this screen some other way would be looking at an empty one.
 *
 * Net profit lives here and nowhere else. Revenue is already on Analytics,
 * which every worker can open; putting the margin there too would leak the
 * cost side straight back out through the screen this one exists to gate.
 */
export function ExpensesScreen({ contentPadding }: { contentPadding: number }) {
  const colors = useColors();
  const t = useT();
  const periodLabel = usePeriodLabel();
  const orders = useAppStore((s) => s.orders);
  const expenses = useExpenses();
  const heads = useExpenseCategories(true);
  const headLabel = useExpenseHeadLabel();
  const headIcon = useExpenseHeadIcon();

  const [period, setPeriod] = useState<Period>('month');
  // Defaults to the last week so the custom tab is never empty on first open.
  const [range, setRange] = useState<DateRange>(() => ({
    start: daysAgo(6),
    end: toDateInput(new Date()),
  }));
  const [editor, setEditor] = useState<{ open: boolean; expense: Expense | null }>({
    open: false,
    expense: null,
  });
  const [headEditor, setHeadEditor] = useState<{
    open: boolean;
    head: ExpenseCategory | null;
  }>({ open: false, head: null });
  const [showHeads, setShowHeads] = useState(false);

  // Both sides are scoped in one place, from one `now`, so the revenue figure
  // and the spending figure cannot describe windows a millisecond apart across
  // a midnight boundary — and so net profit is never a subtraction of two
  // different periods.
  const { inPeriod, series, seriesMax, spent, profit } = useMemo(() => {
    const now = new Date();
    const { from, to } = periodRange(period, now, range);
    const scoped = scopeToPeriod(orders, expenses, from, to);
    const buckets = buildExpenseSeries(scoped.expenses, period, now, range);
    return {
      inPeriod: scoped.expenses,
      series: buckets,
      seriesMax: maxBucketRevenue(buckets),
      spent: summariseExpenses(scoped.expenses),
      profit: profitFor(scoped.orders, scoped.expenses),
    };
  }, [orders, expenses, period, range]);

  const byHead = useMemo(
    () => expenseTotalsByHead(inPeriod, heads),
    [inPeriod, heads],
  );
  const headMax = maxHeadTotal(byHead);

  // A loss is not a smaller profit, it is a different fact, so it gets its own
  // label and the warning colour rather than a minus sign to be missed.
  const atLoss = profit.net < 0;

  return (
    <Screen contentPadding={contentPadding}>
      <PeriodTabs period={period} onChange={setPeriod} testIDPrefix="exp-period" />
      {period === 'custom' ? <RangePicker range={range} onChange={setRange} /> : null}

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
        <StatTile value={fmt(profit.spent)} label={t('spent_in_period')} icon="wallet" />
        <StatTile value={fmt(profit.revenue)} label={t('revenue_in_period')} icon="rupee" />
        <StatTile
          value={fmt(Math.abs(profit.net))}
          label={atLoss ? t('net_loss') : t('net_profit')}
          icon="analytics"
        />
        <StatTile
          value={String(spent.entries)}
          label={t('entries_in_period')}
          icon="note"
        />
      </View>

      {/* The margin, spelled out under the tiles rather than crammed into one.
          "₹4,120 · 18% margin" is the sentence an owner actually wants. */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12 }}>
        <Icon
          name={atLoss ? 'offers' : 'circleCheck'}
          size={15}
          color={atLoss ? colors.spice : colors.good}
        />
        <T size={12.5} color={atLoss ? colors.spice : colors.textDim}>
          {profit.marginPct == null
            ? t('no_margin_yet')
            : t('margin_pct', { pct: Math.round(profit.marginPct) })}
        </T>
      </View>

      <SectionTitle title={t('spend_over_time')} icon="analytics" />
      <BarChart
        buckets={series}
        max={seriesMax}
        emptyLabel={t('no_spend_in_period')}
        periodLabel={periodLabel(period, range)}
        ordersLabel={(n) => (n === 1 ? t('entry_1') : `${n} ${t('entries_word')}`)}
      />

      <SectionTitle title={t('by_head')} icon="wallet" />
      {byHead.length ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
          {byHead.map((h) => (
            <View
              key={h.catId || 'none'}
              style={{
                flexGrow: 1,
                flexBasis: 210,
                backgroundColor: colors.surface2,
                borderWidth: 1,
                borderColor: colors.line,
                borderRadius: radius.md,
                paddingVertical: 13,
                paddingHorizontal: 14,
                gap: 8,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
                <Icon name={headIcon(h.catId || null) as IconName} size={16} color={colors.gold} />
                <T size={13} weight={700} numberOfLines={1} style={{ flexShrink: 1 }}>
                  {headLabel(h.catId || null) || t('uncategorised')}
                </T>
                <T size={13} weight={700} color={colors.gold} style={{ marginLeft: 'auto' }}>
                  {fmt(h.total)}
                </T>
              </View>
              <BarTrack fraction={barFraction(h.total, headMax)} />
              <T size={11.5} color={colors.textFaint}>
                {h.entries === 1 ? t('entry_1') : `${h.entries} ${t('entries_word')}`}
              </T>
            </View>
          ))}
        </View>
      ) : (
        <Panel>
          <EmptyNote>{t('no_spend_in_period')}</EmptyNote>
        </Panel>
      )}

      <SectionTitle title={t('all_entries')} icon="clock" />
      {inPeriod.length ? (
        <View>
          {inPeriod.map((e) => (
            <ExpenseRow
              key={e.id}
              expense={e}
              onPress={() => setEditor({ open: true, expense: e })}
            />
          ))}
        </View>
      ) : (
        <Panel>
          <EmptyNote>{t('no_spend_in_period')}</EmptyNote>
        </Panel>
      )}

      <AddButton
        label={t('add_expense')}
        testID="add-expense"
        onPress={() => setEditor({ open: true, expense: null })}
      />

      {/* Collapsed by default. Editing the heads is a once-a-year job and
          recording a payment is a daily one, so the daily one is not made to
          scroll past the other. */}
      <SectionTitle
        title={t('manage_heads')}
        icon="settings"
        hint={t('manage_heads_hint')}
        hintIcon={showHeads ? 'chevronDown' : 'chevronRight'}
        hintColor={colors.gold}
        onHintPress={() => setShowHeads((v) => !v)}
      />
      {showHeads ? (
        <View>
          {heads.length ? (
            heads.map((h) => (
              <HeadRow
                key={h.id}
                head={h}
                onEdit={() => setHeadEditor({ open: true, head: h })}
              />
            ))
          ) : (
            <EmptyNote>{t('no_heads_yet')}</EmptyNote>
          )}
          <AddButton
            label={t('add_head')}
            testID="add-expense-head"
            onPress={() => setHeadEditor({ open: true, head: null })}
          />
        </View>
      ) : null}

      <ExpenseEditor
        visible={editor.open}
        expense={editor.expense}
        onClose={() => setEditor({ open: false, expense: null })}
      />
      <ExpenseHeadEditor
        visible={headEditor.open}
        head={headEditor.head}
        onClose={() => setHeadEditor({ open: false, head: null })}
      />
    </Screen>
  );
}
